// The quest system — Toast Town's RPG spine. Villagers hand out quests, you work
// the objective through ordinary play (chopping, gathering, exploring), then turn
// it in for crumbs and unlocks. Progress persists through core/save.js; scenes
// talk to this module, never to the save shape directly.
//
// Adding a quest: append an entry to QUESTS. Give it a `giver` (an NPC id a scene
// registers a quest-giver for), a `title`/`desc`, one `objective`, and a `reward`.
// Optional `requires` gates it behind another quest; `next` is a hint for chains;
// `onComplete(api)` runs side effects (e.g. open a gate) when it's turned in.
//
// Objective kinds:
//   count { event, goal }   — accrues when a scene calls progress(event, n)
//                             (e.g. progress('chop', 1) each felled tree)
//   have  { resource, goal } — satisfied while you hold `goal` of a resource;
//                             turning in SPENDS it (a delivery quest)
//   flag  { flag }          — satisfied once a scene calls flagObjective(id, flag)
//                             (e.g. reaching a place)

import {
  getQuests, save, addCrumbs, getLumber, spendLumber, setFlag, getFlag, getState
} from './save.js';
import { addFound } from './collection.js';

// ---- registry -----------------------------------------------------------

export const QUESTS = {
  // ---- THE SPACE BREAD PROGRAM (Mechanic Rivet → dog pirates → cheese vendors → Moon King) ----
  'space-inspect': {
    giver: 'mechanic',
    title: 'Grounded',
    desc: 'The old rocket is a wreck. Inspect the damage with Mechanic Rivet at the launch pad.',
    objective: { kind: 'flag', flag: 'shipInspected', noun: 'inspect the ship' },
    reward: { crumbs: 15 }, next: 'space-radar'
  },
  'space-radar': {
    giver: 'mechanic', requires: 'space-inspect',
    title: 'Eyes in the Dark',
    desc: 'Win a radar crystal at the Asteroid Radar console, then install it in the ship.',
    objective: { kind: 'flag', flag: 'radarInstalled', noun: 'install the radar' },
    reward: { crumbs: 25 }, next: 'space-hull'
  },
  'space-hull': {
    giver: 'mechanic', requires: 'space-radar',
    title: 'Patch Job',
    desc: 'Scruff Barkbeard has space tape. Get it from him and patch the hull.',
    objective: { kind: 'flag', flag: 'hullPatched', noun: 'patch the hull' },
    reward: { crumbs: 25 }, next: 'space-engine'
  },
  'space-engine': {
    giver: 'mechanic', requires: 'space-hull',
    title: 'Heart of the Ship',
    desc: 'Buy an engine core from the cheese vendor stall and install it.',
    objective: { kind: 'flag', flag: 'engineInstalled', noun: 'install the engine core' },
    reward: { crumbs: 30 }, next: 'space-launch'
  },
  'space-launch': {
    giver: 'mechanic', requires: 'space-engine',
    title: 'To the Moon',
    desc: 'Radar, hull, engine — all fixed. Board the rocket and launch to the moon!',
    objective: { kind: 'flag', flag: 'shipLaunched', noun: 'launch to the moon' },
    reward: { crumbs: 40 }, next: 'moon-fuel',
    onComplete(){ setFlag('moonUnlocked', true); }
  },
  'moon-fuel': {
    giver: 'cheesemonger', requires: 'space-launch',
    title: 'Running on Empty',
    desc: 'Buy Moon Cheese Fuel at the lunar cheese shop and refuel the ship.',
    objective: { kind: 'flag', flag: 'shipRefueled', noun: 'refuel the ship' },
    reward: { crumbs: 30 }, next: 'moon-king'
  },
  'moon-king': {
    giver: 'moonking', requires: 'moon-fuel',
    title: 'Audience with the Moon King',
    desc: 'Cook a moon dish for the Moon King in the castle kitchen to earn the navigation chip.',
    objective: { kind: 'flag', flag: 'cookedForKing', noun: 'cook for the king' },
    reward: { crumbs: 50 }, next: 'space-final',
    onComplete(){ setFlag('gotNavChip', true); }
  },
  'space-final': {
    giver: 'moonking', requires: 'moon-king',
    title: 'Fully Operational',
    desc: 'Install the royal nav chip at the ship. The Starship Toaster flies again!',
    objective: { kind: 'flag', flag: 'navInstalled', noun: 'install the nav chip' },
    reward: { crumbs: 100 },
    onComplete(){ setFlag('shipFullyRepaired', true); }
  },
  'garys-stabilizer': {
    giver: 'gary', requires: 'space-final',
    title: 'Gary\'s Stabilizer',
    desc: 'Gary\'s ship broke down in Toast Park. The Moon King may have a compatible lunar stabilizer. Bring it back to Gary at night.',
    objective: { kind: 'flag', flag: 'garyShipFixed', noun: 'return the lunar stabilizer' },
    reward: { crumbs: 75 },
    onComplete(){ setFlag('garyShipFixed', true); }
  },

  // ---- THE CROVIAN SYSTEM (Slice & the Dog Pirates) ----------------------
  'crovian-welcome': {
    giver: 'slice', requires: 'space-final',
    title: 'Welcome to BZ-943',
    desc: 'The Starship Toaster can reach Gary\'s home system. Fly to the Crovian System and meet the Dog Pirates.',
    objective: { kind: 'flag', flag: 'visitedCrovian', noun: 'reach the Crovian System' },
    reward: { crumbs: 30 },
    next: 'loco-burger'
  },
  'loco-burger': {
    giver: 'slice', requires: 'crovian-welcome',
    title: 'Intern Fuel',
    desc: 'Chawar is starving. Get him a Loco Burger from Tito\'s Farm Emporium.',
    objective: { kind: 'flag', flag: 'fedChawar', noun: 'buy Chawar a Loco Burger' },
    reward: { crumbs: 25 },
    next: 'quadra-valve'
  },
  'quadra-valve': {
    giver: 'slice', requires: 'loco-burger',
    title: 'The Quadra-Valve',
    desc: 'Tito has one quadra-valve left. Buy it so Gary can repair the Pirates\' hyperdrive.',
    objective: { kind: 'flag', flag: 'deliveredQuadraValve', noun: 'deliver the quadra-valve' },
    reward: { crumbs: 50 },
    next: 'intern-gary',
    onComplete(){
      addFound('relic-quadra-valve');
      setFlag('garyJoinedPirates', true);
    }
  },
  'intern-gary': {
    giver: 'slice', requires: 'quadra-valve',
    title: 'New Intern Orientation',
    desc: 'Gary joined the crew — HR says we can\'t say "cabin boy" anymore. Check the Museum for your thank-you gift.',
    objective: { kind: 'flag', flag: 'viewedInternBadge', noun: 'inspect the intern badge at the Museum' },
    reward: { crumbs: 25 }
  },

  // ---- FARM QUESTS (Farmer Grain) -----------------------------------------
  'farm-first-harvest': {
    giver: 'farmer',
    title: 'First Harvest',
    desc: 'Plant and harvest your first crop at Sunnybrook Farm.',
    objective: { kind: 'flag', flag: 'harvestedCrop', noun: 'harvest a crop' },
    reward: { crumbs: 15 },
    next: 'farm-delivery-wheat'
  },
  'farm-delivery-wheat': {
    giver: 'farmer',
    requires: 'farm-first-harvest',
    title: 'Grain Delivery',
    desc: 'Bring 3 wheat from the farm. Chip at the Pantry will buy the lot.',
    objective: { kind: 'have', resource: 'crop:wheat', goal: 3, noun: 'wheat' },
    reward: { crumbs: 40 }
  },
  'pantry-stocking': {
    giver: 'clerk',
    requires: 'farm-first-harvest',
    title: 'Stock the Pantry',
    desc: 'Chip needs 3 wheat to bake fresh bread. Bring them to the store.',
    objective: { kind: 'have', resource: 'crop:wheat', goal: 3, noun: 'wheat' },
    reward: { crumbs: 40 }
  },

  // ---- TOWN QUESTS (Mayor Muffin) -----------------------------------------
  'town-booster': {
    giver: 'mayor',
    title: 'A Toastier Town',
    desc: 'Spend crumbs to upgrade any building in the town square to level 2.',
    objective: { kind: 'flag', flag: 'upgradedBuilding', noun: 'upgrade a building' },
    reward: { crumbs: 25 },
    next: 'dinner-rush'
  },
  'dinner-rush': {
    giver: 'mayor',
    requires: 'town-booster',
    title: 'First Shift',
    desc: 'Help out at The Toasted Table. Complete one dinner rush service.',
    objective: { kind: 'flag', flag: 'completedOrderRush', noun: 'complete Order Rush' },
    reward: { crumbs: 35 },
    next: 'woodland-helper'
  },
  'woodland-helper': {
    giver: 'mayor',
    requires: 'dinner-rush',
    title: 'Bridge Supplies',
    desc: 'The forest trail needs lumber. Bring Mayor Muffin 5 lumber.',
    objective: { kind: 'have', resource: 'lumber', goal: 5, noun: 'lumber' },
    reward: { crumbs: 50 },
    onComplete(){ setFlag('fortressInvite', true); }
  },

  // ---- FOREST QUESTS (Ranger Rusk) ----------------------------------------
  'chop-firewood': {
    giver: 'ranger',
    title: 'A Splinter of a Problem',
    desc: "Ranger Rusk's stove is cold. Chop 3 trees at the big oak by the trail.",
    objective: { kind: 'count', event: 'chop', goal: 3, noun: 'trees chopped' },
    reward: { crumbs: 20 },
    next: 'haul-lumber'
  },
  'haul-lumber': {
    giver: 'ranger',
    requires: 'chop-firewood',
    title: 'Shore Up the Bridge',
    desc: 'The old trail bridge is sagging. Bring Ranger Rusk 8 lumber to mend it.',
    objective: { kind: 'have', resource: 'lumber', goal: 8, noun: 'lumber' },
    reward: { crumbs: 45 },
    next: 'reach-fortress',
    // mending the bridge unlatches the Fortress gate deep in the woods
    onComplete(){ setFlag('fortressOpen', true); }
  },
  'reach-fortress': {
    giver: 'ranger',
    requires: 'haul-lumber',
    title: 'The Sealed Gate',
    desc: 'With the bridge fixed, the path to the Fortress is clear. Go and see if its gate finally gives.',
    objective: { kind: 'flag', flag: 'reachedFortress', noun: 'reach the Fortress' },
    reward: { crumbs: 60 }
  },

  // ---- REC CENTER QUESTS (Coach Crust) ------------------------------------
  'rec-routine': {
    giver: 'coach',
    title: 'Gym Rat Initiation',
    desc: 'Coach Crust wants to see you break a sweat. Try three different machines at the Rec Center.',
    objective: { kind: 'count', event: 'recUse', goal: 3, noun: 'machines used' },
    reward: { crumbs: 35 }
  },

  // ---- PARK QUESTS (Gardener Glen) ----------------------------------------
  'green-thumb': {
    giver: 'gardener',
    title: 'Green Thumb',
    desc: 'Beautify Toast Park. Plant 3 flower beds and 1 tree.',
    objective: { kind: 'flag', flag: 'greenThumbComplete', noun: 'plant 3 flowers & 1 tree' },
    reward: { crumbs: 45 }
  },

  // ---- GRAVEYARD QUEST (Moldy Moe) ----------------------------------------
  'moldy-moe': {
    giver: 'moldymoe',
    title: "Moldy Moe's Last Crumb",
    desc: 'A ghost in the Forest Graveyard lost his burial crumb. Dig it up and return it to him.',
    objective: { kind: 'flag', flag: 'moldyMoeCrumbFound', noun: 'find the burial crumb' },
    reward: { crumbs: 60 },
    onComplete(){ setFlag('moldyMoeComplete', true); }
  },

  // ---- SHERIFF STATION QUESTS (Deputy Donut) -------------------------------
  'station-supplies': {
    giver: 'deputy',
    title: 'Station Supplies',
    desc: 'The jail bars are rusted. Bring Deputy Donut 3 lumber to fix them up.',
    objective: { kind: 'have', resource: 'lumber', goal: 3, noun: 'lumber' },
    reward: { crumbs: 35 }
  },

  // ---- BANK QUESTS (Teller Toast) ------------------------------------------
  'bank-deposit': {
    giver: 'teller',
    title: 'First Deposit',
    desc: 'Open a savings account at The Bread Bank. Deposit 50 crumbs to earn interest.',
    objective: { kind: 'flag', flag: 'madeBankDeposit', noun: 'make a 50-crumb deposit' },
    reward: { crumbs: 75 },
    next: 'bank-withdraw'
  },
  'bank-withdraw': {
    giver: 'teller', requires: 'bank-deposit',
    title: 'Test the Vault',
    desc: 'Withdraw your savings from The Bread Bank to make sure the vault works.',
    objective: { kind: 'flag', flag: 'madeBankWithdraw', noun: 'withdraw savings' },
    reward: { crumbs: 25 },
    next: 'bank-interest'
  },
  'bank-interest': {
    giver: 'teller', requires: 'bank-withdraw',
    title: 'Compound Crumbs',
    desc: 'Return to The Bread Bank on a new day to collect interest.',
    objective: { kind: 'flag', flag: 'claimedBankInterest', noun: 'collect interest' },
    reward: { crumbs: 50 }
  },

  // ---- COOKING QUEST (Mayor Muffin) ----------------------------------------
  'chef-in-training': {
    giver: 'mayor',
    title: 'Chef in Training',
    desc: 'Cook your first dish at the stove in Bread Boi\'s house.',
    objective: { kind: 'flag', flag: 'cookedFirstDish', noun: 'cook a dish' },
    reward: { crumbs: 30 }
  },

  // ---- TOASTER QUEST (Mayor Muffin) ----------------------------------------
  'toaster-run': {
    giver: 'mayor',
    title: 'Into the Toaster',
    desc: 'Survive the Infinite Toaster long enough to reach floor 5.',
    objective: { kind: 'flag', flag: 'reachedToasterFloor5', noun: 'reach floor 5' },
    reward: { crumbs: 80 }
  },

  // ---- UPTOWN QUESTS -------------------------------------------------------
  'dojo-train': {
    giver: 'riceboi',
    title: 'Way of the Grain',
    desc: 'Train with Rice Boi and break your first board.',
    objective: { kind: 'flag', flag: 'brokeBoard', noun: 'board broken' },
    reward: { crumbs: 30 },
    next: 'dojo-master'
  },
  'dojo-master': {
    giver: 'riceboi',
    requires: 'dojo-train',
    title: 'Black Belt Crust',
    desc: 'Score 3 perfect breaks in one round.',
    objective: { kind: 'flag', flag: 'perfectRound', noun: '3 perfect breaks' },
    reward: { crumbs: 100 }
  },
  'casino-try': {
    giver: 'dealer',
    title: 'Lucky Slice',
    desc: 'Play any game in the casino.',
    objective: { kind: 'flag', flag: 'playedCasino', noun: 'game played' },
    reward: { crumbs: 20 }
  },
  'apt-decor': {
    giver: 'superintendent',
    title: 'Home Sweet Home',
    desc: 'Buy one piece of apartment furniture.',
    objective: { kind: 'flag', flag: 'boughtFurniture', noun: 'furniture bought' },
    reward: { crumbs: 25 }
  },
  'first-car': {
    giver: 'pretzel',
    title: 'Get Toasted',
    desc: 'Buy your first car at the garage.',
    objective: { kind: 'flag', flag: 'boughtCar', noun: 'car bought' },
    reward: { crumbs: 40 }
  },
  'grill-first-flip': {
    giver: 'bigt',
    title: 'Flip Like Big T',
    desc: 'Work a shift at Big T\'s Grill and plate your first patty.',
    objective: { kind: 'flag', flag: 'flippedFirstPatty', noun: 'plate a patty' },
    reward: { crumbs: 30 }
  },

  // ---- BEACH QUESTS (Angler Andy) ------------------------------------------
  'first-catch': {
    giver: 'angler',
    title: 'First Catch',
    desc: 'Cast a line off the pier and reel in your first fish.',
    objective: { kind: 'flag', flag: 'caughtFirstFish', noun: 'catch a fish' },
    reward: { crumbs: 25 },
    next: 'catch-five'
  },
  'catch-five': {
    giver: 'angler',
    requires: 'first-catch',
    title: 'Gone Fishin\'',
    desc: 'Reel in 5 fish from the Toast Town shore.',
    objective: { kind: 'count', event: 'fishCaught', goal: 5, noun: 'fish caught' },
    reward: { crumbs: 60 }
  },

  // ---- AQUARIUM QUEST (Marina) ---------------------------------------------
  'aquarium-donate': {
    giver: 'marina',
    title: 'Tank You Very Much',
    desc: 'Donate 3 different fish to the Toast Town Aquarium.',
    objective: { kind: 'flag', flag: 'donatedThreeFish', noun: 'donate 3 fish' },
    reward: { crumbs: 80 }
  },

  // ---- BANANA VILLAGE QUEST (Chiquita) -------------------------------------
  'visit-banana-village': {
    giver: 'chiquita',
    title: 'Peel of Fortune',
    desc: 'Take the road east and visit the Banana Village.',
    objective: { kind: 'flag', flag: 'visitedBananaVillage', noun: 'visit Banana Village' },
    reward: { crumbs: 40 }
  },

  // ---- SEASONAL QUEST (Mayor Muffin) ---------------------------------------
  'seasonal-harvest': {
    giver: 'mayor',
    title: 'Seasonal Harvest',
    desc: 'Harvest a crop during its preferred season for a bonus.',
    objective: { kind: 'flag', flag: 'harvestedInSeason', noun: 'harvest in-season crop' },
    reward: { crumbs: 35 }
  },

  // ---- THE BAKER KING'S CRUST (multi-act storyline) ------------------------
  // Act I: Rumors in the Crumbs
  'crust-archives': {
    giver: 'mayor',
    title: 'Rumors in the Crumbs',
    desc: 'Read the three lore plaques in the Toast Town Museum to learn about the Baker King.',
    objective: { kind: 'flag', flag: 'readMuseumLore', noun: 'read all museum plaques' },
    reward: { crumbs: 25 },
    next: 'rangers-warning'
  },
  'rangers-warning': {
    giver: 'ranger',
    requires: 'crust-archives',
    title: "Ranger's Warning",
    desc: 'The Fortress gate has been acting up since the bridge was mended. Listen to Ranger Rusk.',
    objective: { kind: 'flag', flag: 'heardRangerWarning', noun: 'hear the warning' },
    reward: { crumbs: 30 },
    next: 'find-the-keystone'
  },
  // Act II: The Sealed Fortress
  'find-the-keystone': {
    giver: 'ranger',
    requires: 'rangers-warning',
    title: 'Find the Keystone',
    desc: 'The Baker King sealed the inner chamber with a stone sigil. Dig up the three broken pieces in the Forest.',
    objective: { kind: 'count', event: 'keystoneFragment', goal: 3, noun: 'keystone fragments' },
    reward: { crumbs: 60 },
    next: 'moes-memory'
  },
  'moes-memory': {
    giver: 'moldymoe',
    requires: 'find-the-keystone',
    title: "Moe's Memory",
    desc: 'Moldy Moe remembers where the keystone belongs. Help him recall by returning his burial crumb.',
    objective: { kind: 'flag', flag: 'moldyMoeComplete', noun: 'return Moe\'s crumb' },
    reward: { crumbs: 45 },
    next: 'fortress-reopened'
  },
  'fortress-reopened': {
    giver: 'ranger',
    requires: 'moes-memory',
    title: 'Fortress Reopened',
    desc: 'With the sigil restored, enter the Baker King\'s chamber deep in the Fortress.',
    objective: { kind: 'flag', flag: 'reachedKingsChamber', noun: 'reach the King\'s chamber' },
    reward: { crumbs: 80 },
    next: 'crown-the-crumb'
  },
  // Act III: The Golden Loaf
  'crown-the-crumb': {
    giver: 'kingbread',
    requires: 'fortress-reopened',
    title: 'Crown the Crumb',
    desc: 'The Baker King needs his royal regalia. Find the Crown, Scepter, Gauntlet, and Bread-Bone in the Museum collection.',
    objective: { kind: 'flag', flag: 'collectedRoyalRegalia', noun: 'collect the royal regalia' },
    reward: { crumbs: 100 },
    next: 'ceremony-of-toast'
  },
  'ceremony-of-toast': {
    giver: 'kingbread',
    requires: 'crown-the-crumb',
    title: 'Ceremony of Toast',
    desc: 'Cook a Royal Tart and bring it to the altar in the King\'s chamber to restore the Golden Loaf.',
    objective: { kind: 'flag', flag: 'performedToastCeremony', noun: 'perform the ceremony' },
    reward: { crumbs: 150 },
    // The Baker King has no accept dialog — the throne scene turns his quests in
    // but never offers them — so without this `next` the pilgrimage can never be
    // started and the arc has no ending.
    next: 'pilgrimage-to-king',
    onComplete(){
      setFlag('bakerKingStatuePlaced', true);
      setFlag('bakerKingBlessing', true);
    }
  },
  'pilgrimage-to-king': {
    giver: 'kingbread',
    requires: 'ceremony-of-toast',
    title: 'Pilgrimage to the King',
    desc: 'Visit the Baker King statue in the town square to receive his daily blessing.',
    objective: { kind: 'flag', flag: 'visitedKingStatueToday', noun: 'visit the statue' },
    reward: { crumbs: 25 }
  }
};

// ---- tiny event bus (HUD listens for toasts/log refresh) ---------------

const listeners = new Set();
export function onQuestEvent(fn){ listeners.add(fn); return () => listeners.delete(fn); }
function emit(type, quest){ listeners.forEach(fn => fn({ type, quest })); }

// ---- status queries -----------------------------------------------------

export function isCompleted(id){ return getQuests().completed.includes(id); }
export function isActive(id){ return id in getQuests().active; }

export const isQuestComplete = isCompleted;
export const isQuestStarted = isActive;

// prereqs met and not yet started/finished
export function isAvailable(id){
  const q = QUESTS[id];
  if (!q || isActive(id) || isCompleted(id)) return false;
  return !q.requires || isCompleted(q.requires);
}

// how far along an active quest's objective is (0..goal) and whether it's met
export function objectiveProgress(id){
  const q = QUESTS[id];
  const live = getQuests().active[id];
  if (!q || !live) return { have: 0, goal: 0, ready: false };
  const o = q.objective;
  let have = 0;
  if (o.kind === 'count')      have = Math.min(live.count || 0, o.goal);
  else if (o.kind === 'have')  have = Math.min(getResourceAmount(o.resource), o.goal);
  else if (o.kind === 'flag')  have = (live.flags?.[o.flag] || getFlag(o.flag)) ? 1 : 0;
  const goal = o.kind === 'flag' ? 1 : o.goal;
  return { have, goal, ready: have >= goal };
}

export function isReady(id){ return objectiveProgress(id).ready; }

function getResourceAmount(resource){
  // resources: 'lumber', 'crop:<id>', 'seed:<id>'
  if (resource === 'lumber') return getLumber();
  if (resource.startsWith('crop:')){
    const id = resource.slice(5);
    return getState().inventory.crops[id] || 0;
  }
  if (resource.startsWith('seed:')){
    const id = resource.slice(5);
    return getState().inventory.seeds[id] || 0;
  }
  return 0;
}

// ---- the quests a given NPC has to offer/turn in right now --------------

// Returns { available, active } quest ids for a giver, so a scene can present
// "accept this" / "turn this in" without knowing the registry shape.
export function questsForGiver(giver){
  const available = [], active = [];
  for (const id in QUESTS){
    if (QUESTS[id].giver !== giver) continue;
    if (isActive(id)) active.push(id);
    else if (isAvailable(id)) available.push(id);
  }
  return { available, active };
}

// ---- mutations ----------------------------------------------------------

export function acceptQuest(id){
  if (!isAvailable(id)) return false;
  getQuests().active[id] = { count: 0, flags: {} };
  save();
  emit('accepted', id);
  return true;
}

export const startQuest = acceptQuest;

// A scene reports a countable action (e.g. felled a tree). Bumps every active
// count-quest listening for that event and emits when one becomes ready.
export function progress(event, amount = 1){
  const active = getQuests().active;
  let changed = false;
  for (const id in active){
    const o = QUESTS[id]?.objective;
    if (o?.kind !== 'count' || o.event !== event) continue;
    active[id].count = (active[id].count || 0) + amount;
    changed = true;
    emit(isReady(id) ? 'ready' : 'progress', id);
  }
  if (changed) save();
}

// A scene reports a place/state was reached (flag objective).
export function flagObjective(id, flag){
  const live = getQuests().active[id];
  const q = QUESTS[id];
  if (!live || q?.objective?.kind !== 'flag' || q.objective.flag !== flag) return;
  if (live.flags?.[flag] && getFlag(flag)) return;
  live.flags = { ...live.flags, [flag]: true };
  setFlag(flag, true);
  save();
  emit('ready', id);
}

// Turn a ready quest in: spend delivery resources, grant reward, fire side
// effects, mark completed. Returns the reward granted, or null if not ready.
export function turnIn(id){
  if (!isActive(id) || !isReady(id)) return null;
  const q = QUESTS[id];
  const o = q.objective;
  if (o.kind === 'have'){
    if (o.resource === 'lumber'){
      if (!spendLumber(o.goal)) return null;
    } else if (o.resource.startsWith('crop:')){
      const id = o.resource.slice(5);
      const have = getState().inventory.crops[id] || 0;
      if (have < o.goal) return null;
      getState().inventory.crops[id] = have - o.goal;
      save();
    } else {
      return null;
    }
  }
  delete getQuests().active[id];
  getQuests().completed.push(id);
  if (q.reward?.crumbs) addCrumbs(q.reward.crumbs);
  q.onComplete?.();
  // auto-accept the next step in a chain if it has no NPC dialog gate
  if (q.next && QUESTS[q.next] && isAvailable(q.next)){
    acceptQuest(q.next);
  }
  save();
  emit('completed', id);
  return q.reward || {};
}

export const completeQuest = turnIn;

// ---- for the HUD quest log: every active quest with its live progress ---
export function activeQuestList(){
  return Object.keys(getQuests().active)
    .filter(id => QUESTS[id])          // ignore stale/unknown quest ids
    .map(id => ({
      id, ...QUESTS[id], ...objectiveProgress(id)
    }));
}
